import { renderOnRequestDurations } from '../../services/calcDuration.js';
import { CourseHighlight } from '../../components/index';
import styled from 'styled-components';
import { TravelHighlight } from './TravelHighlight';
import { LocationHighlight } from './LocationHighlight';
import { CourseDurationHighlight } from './CourseDurationHighlight';
import { Environment } from 'services/Environment';

const Wrapper = styled.section`
    @media (min-width: 767px) {
        display: grid;
        grid-template-columns: 50% 50%;
    }

    @media (max-width: 767px) {
        grid-template-columns: auto;
    }
`;

const DELIVERY_ICON = {
    'in-person': `${Environment.ASSETS_URL}/icons/users-class-light.svg`,
    'on-demand': `${Environment.ASSETS_URL}/icons/laptop-light.svg`,
    online: `${Environment.ASSETS_URL}/icons/camera-light.svg`,
    'on-request': `${Environment.ASSETS_URL}/icons/handshake-light.svg`,
    hybrid: `${Environment.ASSETS_URL}/icons/house-laptop-light.svg`,
};

const renderOnRequestLabel = (course = {}) => {
    try {
        const deliveryOptions = course.delivery_config.map((dc) => dc.delivery);
        let uniqueOptions = [...new Set(deliveryOptions)];
        if (uniqueOptions.length === 2) {
            return `Delivered ${uniqueOptions[0]} & ${uniqueOptions[1]}`;
        } else {
            return `Delivered online, in-person & on request`;
        }
    } catch (err) {
        return null;
    }
};

const renderDelivery = (course = {}) => {
    if (course.type === 'on-request') {
        if (course.available_for_in_person_delivery && course.available_for_online_delivery) {
            return (
                <CourseHighlight description="Delivered online or in-person">
                    <img src={DELIVERY_ICON.hybrid} height="26px" alt="Hybrid class icon" />
                </CourseHighlight>
            );
        }
        if (course.available_for_in_person_delivery) {
            return (
                <CourseHighlight description="Delivered in-person">
                    <img src={DELIVERY_ICON['in-person']} height="26px" alt="Delivered in-person" />
                </CourseHighlight>
            );
        }
        if (course.available_for_online_delivery) {
            return (
                <CourseHighlight description="Delivered online">
                    <img src={DELIVERY_ICON.online} height="26px" alt="Online class icon" />
                </CourseHighlight>
            );
        }
        return null;
    }

    if (course.delivery === 'in-person') {
        return (
            <CourseHighlight description="Delivered in-person">
                <img src={DELIVERY_ICON[course.delivery]} height="26px" alt="Delivered in-person" />
            </CourseHighlight>
        );
    }

    if (course.type === 'on-demand') {
        return (
            <CourseHighlight description={`On-demand ${course.keyword || 'course'}`}>
                <img
                    src={DELIVERY_ICON[course.delivery]}
                    height="26px"
                    alt="Online self-paced programme"
                />
            </CourseHighlight>
        );
    }

    if (course.delivery === 'online' && course.type !== 'on-demand') {
        return (
            <CourseHighlight description="Delivered online">
                <img src={DELIVERY_ICON[course.delivery]} height="26px" alt="Online class icon" />
            </CourseHighlight>
        );
    }

    if (course.delivery === 'hybrid' && course.type !== 'on-demand') {
        return (
            <CourseHighlight description="Delivered in-person & online">
                <img src={DELIVERY_ICON[course.delivery]} height="26px" alt="Hybrid class icon" />
            </CourseHighlight>
        );
    }

    if (course.delivery === 'on-request' && course.delivery_config.length) {
        if (course.delivery_config.length === 1) {
            return (
                <CourseHighlight description={`Delivered ${course.delivery_config[0].delivery}`}>
                    <img
                        src={DELIVERY_ICON[course.delivery_config[0].delivery]}
                        height="26px"
                        alt={`Course delivered ${course.delivery_config[0].delivery}`}
                    />
                </CourseHighlight>
            );
        } else {
            return (
                <CourseHighlight description={renderOnRequestLabel(course)}>
                    <img
                        src={`${Environment.ASSETS_URL}/icons/handshake-light.svg`}
                        height="26px"
                        alt={renderOnRequestLabel(course)}
                    />
                </CourseHighlight>
            );
        }
    }

    return null;
};

export const CourseHighlights = ({ course }) => {
    return (
        <Wrapper>
            {renderDelivery(course)}
            <TravelHighlight course={course} />
            <LocationHighlight course={course} />
            {course.level ? (
                <CourseHighlight
                    description={course.level === 'All' ? 'All levels' : course.level + ' level'}
                >
                    <img
                        src={`${Environment.ASSETS_URL}/icons/ruler.svg`}
                        height="26px"
                        alt="Ruler icon"
                    />
                </CourseHighlight>
            ) : null}
            {course.spaces ? (
                <CourseHighlight
                    description={
                        course.spaces > 1
                            ? `Up to ${course.spaces} people in class`
                            : 'Delivered 1 on 1'
                    }
                >
                    <img
                        src={`${Environment.ASSETS_URL}/icons/users.svg`}
                        height="26px"
                        alt="people icon"
                    />
                </CourseHighlight>
            ) : null}
            {course.type === 'on-request' && course.duration_pricing ? (
                <CourseHighlight description={renderOnRequestDurations(course.duration_pricing)}>
                    <img
                        src={`${Environment.ASSETS_URL}/icons/clock.svg`}
                        height="26px"
                        alt="clock icon"
                    />
                </CourseHighlight>
            ) : null}
            <CourseDurationHighlight course={course} />
        </Wrapper>
    );
};
